/**
 * @fileoverview SEO configuration derived from customer config.
 * Auto-generates SEO defaults from the customer configuration.
 */

import { customerConfig, type DailyTradingHours, type TradingPeriod } from '@/lib/config/customer.config';
import type {
  SiteSEOConfig,
  PageSEOConfig,
  LocalBusinessSchema,
  OpeningHoursSpec,
  LinkGroup,
  SiteLink,
} from './_types';

/**
 * Day names for schema.org OpeningHoursSpecification
 */
const SCHEMA_DAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/**
 * Generate site-wide SEO configuration from customer config
 */
export function generateSiteSEOConfig(): SiteSEOConfig {
  const { businessName, tagline, description, websiteUrl, theme, socialLinks } =
    customerConfig;

  return {
    defaultTitle: businessName,
    titleTemplate: `%s | ${businessName}`,
    defaultDescription:
      description ||
      tagline ||
      `Welcome to ${businessName}. Book your table online.`,
    defaultKeywords: [
      businessName.toLowerCase(),
      'restaurant',
      'booking',
      'reservation',
      'dining',
      customerConfig.address.city.toLowerCase(),
      customerConfig.address.suburb.toLowerCase(),
    ],
    siteUrl: websiteUrl,
    defaultImage: theme?.logoUrl,
    twitterHandle: socialLinks?.twitter
      ? `@${socialLinks.twitter.split('/').pop()}`
      : undefined,
    locale: 'en_AU',
    themeColor: theme?.primaryColor,
  };
}

/**
 * Site-wide SEO configuration singleton
 */
export const siteSEOConfig = generateSiteSEOConfig();

/**
 * Generate LocalBusiness structured data from customer config
 */
export function generateLocalBusinessSchema(): LocalBusinessSchema {
  const {
    businessName,
    description,
    tagline,
    websiteUrl,
    phone,
    email,
    address,
    tradingHours,
    socialLinks,
    theme,
  } = customerConfig;

  // Convert trading hours to schema.org format
  const openingHours: OpeningHoursSpec[] = tradingHours
    .filter((day: DailyTradingHours) => !day.isClosed && day.periods.length > 0)
    .flatMap((day: DailyTradingHours) =>
      day.periods.map((period: TradingPeriod) => ({
        '@type': 'OpeningHoursSpecification' as const,
        dayOfWeek: SCHEMA_DAYS[day.dayIndex],
        opens: period.open,
        closes: period.close,
      }))
    );

  // Collect social links for sameAs
  const sameAs: string[] = [];
  if (socialLinks?.facebook) sameAs.push(socialLinks.facebook);
  if (socialLinks?.instagram) sameAs.push(socialLinks.instagram);
  if (socialLinks?.twitter) sameAs.push(socialLinks.twitter);
  if (socialLinks?.tiktok) sameAs.push(socialLinks.tiktok);

  return {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    name: businessName,
    description: description || tagline,
    url: websiteUrl,
    telephone: phone,
    email: email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: address.street,
      addressLocality: address.suburb,
      addressRegion: address.state,
      postalCode: address.postalCode,
      addressCountry: address.country,
    },
    openingHoursSpecification: openingHours,
    acceptsReservations: true,
    image: theme?.logoUrl ? `${websiteUrl}${theme.logoUrl}` : undefined,
    sameAs: sameAs.length > 0 ? sameAs : undefined,
  };
}

/**
 * Pre-defined page SEO configurations
 */
export const pageSEOConfigs: Record<string, PageSEOConfig> = {
  home: {
    title: 'Book a Table',
    description: `Reserve your table at ${customerConfig.businessName}. Easy online booking for ${customerConfig.address.suburb}, ${customerConfig.address.city}.`,
    keywords: [
      'book table',
      'restaurant reservation',
      'online booking',
      customerConfig.address.city.toLowerCase(),
    ],
  },
  booking: {
    title: 'Book a Table',
    description: `Make a reservation at ${customerConfig.businessName}. Select your date, time, and party size for an unforgettable dining experience.`,
    keywords: ['reservation', 'book online', 'table booking'],
  },
  manage: {
    title: 'Manage Booking',
    description: `View, edit, or cancel your reservation at ${customerConfig.businessName}.`,
    noIndex: true, // Don't index management pages
  },
  confirmation: {
    title: 'Booking Confirmed',
    description: `Your reservation at ${customerConfig.businessName} has been confirmed.`,
    noIndex: true,
  },
};

/**
 * Get SEO config for a specific page
 */
export function getPageSEOConfig(pageKey: string): PageSEOConfig {
  return pageSEOConfigs[pageKey] || {};
}

/**
 * Generate navigation links from customer config
 */
export function generateSiteLinks(): LinkGroup[] {
  const { phone, email, address, socialLinks } = customerConfig;

  const navigationLinks: SiteLink[] = [
    { label: 'Book a Table', href: '/', description: 'Make a reservation' },
    { label: 'Manage Booking', href: '/manage', description: 'View or edit your booking' },
  ];

  const contactLinks: SiteLink[] = [
    {
      label: phone,
      href: `tel:${phone.replace(/\s/g, '')}`,
      description: 'Call us',
      isExternal: true,
    },
    {
      label: email,
      href: `mailto:${email}`,
      description: 'Email us',
      isExternal: true,
    },
  ];

  if (address.mapsLink) {
    contactLinks.push({
      label: 'Get Directions',
      href: address.mapsLink,
      description: 'View on Google Maps',
      isExternal: true,
    });
  }

  const socialMediaLinks: SiteLink[] = [];
  if (socialLinks?.facebook) {
    socialMediaLinks.push({
      label: 'Facebook',
      href: socialLinks.facebook,
      isExternal: true,
    });
  }
  if (socialLinks?.instagram) {
    socialMediaLinks.push({
      label: 'Instagram',
      href: socialLinks.instagram,
      isExternal: true,
    });
  }
  if (socialLinks?.twitter) {
    socialMediaLinks.push({
      label: 'Twitter',
      href: socialLinks.twitter,
      isExternal: true,
    });
  }
  if (socialLinks?.tiktok) {
    socialMediaLinks.push({
      label: 'TikTok',
      href: socialLinks.tiktok,
      isExternal: true,
    });
  }

  const groups: LinkGroup[] = [
    { title: 'Navigation', links: navigationLinks },
    { title: 'Contact', links: contactLinks },
  ];

  if (socialMediaLinks.length > 0) {
    groups.push({ title: 'Follow Us', links: socialMediaLinks });
  }

  return groups;
}

/**
 * Generate canonical URL for a path
 */
export function generateCanonicalUrl(path: string): string {
  const baseUrl = customerConfig.websiteUrl.replace(/\/$/, '');
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
}

/**
 * Generate robots meta content
 */
export function generateRobotsMeta(config: PageSEOConfig): string {
  const directives: string[] = [];

  if (config.noIndex) {
    directives.push('noindex');
  } else {
    directives.push('index');
  }

  if (config.noFollow) {
    directives.push('nofollow');
  } else {
    directives.push('follow');
  }

  return directives.join(', ');
}
